/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable react-hooks/exhaustive-deps */
import { Box, Flex, useColorMode, useColorStyle } from '@tonic-one/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigRecommendationApi } from '@Apis';
import { useDashboardServicesStore } from 'src/models/useDashboardServicesModel';
import { useOrgStore } from 'src/models/useOrgModel';

import DashboardLayout from '../Layout/DashboardLayout';
import {
  formatReport,
  formatRules,
  formatServices,
  Report,
  Rules,
  ThreatType,
} from './report';
import ReportHeader from './ReportHeader';
import ReportTable from './ReportTable';
import Summary from './Summary';
import { ServiceNameEnum } from '@Types/index';
import Organization from '../../../components/Common/Organization';
import ServiceCheckbox from '../ServiceCheckbox';

const INCLUDE_SERVICES = new Set([
  ServiceNameEnum.Exchange,
  ServiceNameEnum.ExchangeInline,
  ServiceNameEnum.SharePoint,
  ServiceNameEnum.OneDrive,
  ServiceNameEnum.Box,
  ServiceNameEnum.Dropbox,
  ServiceNameEnum.GoogleDrive,
  ServiceNameEnum.SalesforceSandbox,
  ServiceNameEnum.SalesforceProduction,
  ServiceNameEnum.Gmail,
  ServiceNameEnum.GmailInline,
  ServiceNameEnum.Teams,
  ServiceNameEnum.TeamsChat,
]);

const RecommendConfigLayout = () => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation();

  const { switchOrgId, defaultSingleOrgId, orgId } = useOrgStore();
  if (defaultSingleOrgId) {
    switchOrgId(defaultSingleOrgId);
  }
  const svcModel = useDashboardServicesStore();
  const enableServices = formatServices(
    svcModel.enabledServices.filter((x) => INCLUDE_SERVICES.has(x))
  );

  const [rules, setRules] = useState<Rules | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [threatType, setThreatType] = useState<ThreatType>(ThreatType.BEC);

  useEffect(() => {
    ConfigRecommendationApi.getConfigRecommendationRules().then((res) =>
      setRules(formatRules(res.data))
    );
  }, []);

  useEffect(() => {
    if (rules && orgId !== 'all') {
      ConfigRecommendationApi.getConfigRecommendations().then((res) => {
        if (!res.data?.report) {
          onRefresh();
        }
        setReport(formatReport(rules, res.data?.report));
      });
    }
  }, [orgId, rules]);

  const [loading, setLoading] = useState<boolean>(false);
  async function onRefresh() {
    setLoading(true);
    try {
      const genRes = await ConfigRecommendationApi.genConfigRecommendationJob();
      if (!genRes.data?.jobId) {
        throw new Error(`Invalid job id (${genRes.data?.jobId})`);
      }

      let delay = 1;
      let completed = false;
      while (!completed) {
        await new Promise((resolve) => setTimeout(resolve, delay * 1000));
        delay = Math.min(60, delay * 1.5);
        const getRes = await ConfigRecommendationApi.getConfigRecommendationJob(
          genRes.data.jobId
        );
        completed = getRes?.data.completed;
      }

      const reportRes =
        await ConfigRecommendationApi.getConfigRecommendations();
      if (rules) {
        setReport(formatReport(rules, reportRes.data?.report));
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <DashboardLayout
      title={t('recommendConfig.filter.recommendFilter')}
      barColor="#4269CC"
    >
      <Flex
        gridRow="auto/span 1"
        gridColumn="auto/span 2"
        width="100%"
        justify="space-between"
      >
        <Box
          background={
            colorMode === 'dark'
              ? colorStyle.background.tertiary
              : colorStyle.background.primary
          }
          border={colorMode === 'dark' ? '' : '1px solid'}
          borderColor={
            colorMode === 'dark' ? '' : colorStyle.background.tertiary
          }
          width="320px"
        >
          <Summary
            report={report}
            services={enableServices}
            onRefresh={onRefresh}
            refreshing={loading}
          />
        </Box>

        <Box
          background={
            colorMode === 'dark'
              ? colorStyle.background.tertiary
              : colorStyle.background.primary
          }
          border={colorMode === 'dark' ? '' : '1px solid'}
          borderColor={
            colorMode === 'dark' ? '' : colorStyle.background.tertiary
          }
          width="calc(100% - 321px)"
          height="100%"
          mb="24px"
        >
          <ReportHeader
            report={report}
            services={enableServices}
            threatType={threatType}
            onThreatType={setThreatType}
          />

          <ReportTable
            report={report}
            services={enableServices}
            threat={threatType}
          />
        </Box>
      </Flex>
    </DashboardLayout>
  );
};

const RecommendConfig = () => {
  return (
    <>
      <Flex m="24px 16px" gap="24px">
        <Organization allOrgItem={false} />
        <ServiceCheckbox includeServices={INCLUDE_SERVICES} />
      </Flex>

      <Flex width="100%" direction="column" p="0 16px">
        <RecommendConfigLayout />
      </Flex>
    </>
  );
};

export default RecommendConfig;
